import { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import toast from 'react-hot-toast';
import {
  DollarSign, TrendingUp, TrendingDown, Users,
  AlertTriangle, RefreshCw, Clock, CheckCircle,
  XCircle, FileText, CreditCard, Wallet, Shield,
  BarChart2, Activity, UserCheck, UserX
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, decimals = 0) =>
  n != null ? Number(n).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : '0';

const fmtUSD = (n) => '$' + fmt(n, 2);

const METHOD_LABELS = {
  bank_transfer_global:      'Bank Transfer',
  bank_transfer_per_user:    'Bank (Per User)',
  bank_transfer_per_country: 'Bank (Per Country)',
  crypto_payments:           'Crypto',
  payment_links:             'Payment Link',
  bank:                      'Bank Transfer',
};
const methodLabel = (m) => METHOD_LABELS[m] || (m ? m.replace(/_/g, ' ') : '—');

const hourLabel = (h, t) => {
  if (h < 1) return t('time.justNow');
  if (h === 1) return `1 ${t('time.hours')} ${t('time.ago')}`;
  return `${h} ${t('time.hours')} ${t('time.ago')}`;
};

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, subtext, color }) {
  return (
    <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>{label}</p>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold font-mono mb-0.5" style={{ color }}>{value}</p>
      {subtext && <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{subtext}</p>}
    </div>
  );
}

// ─── Metric card ──────────────────────────────────────────────────────────────
function MetricCard({ label, value, icon: Icon, color = 'var(--text-primary)', subtext }) {
  return (
    <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</p>
        <Icon className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
      </div>
      <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
      {subtext && <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{subtext}</p>}
    </div>
  );
}

// ─── Section heading ──────────────────────────────────────────────────────────
function SectionHeading({ icon: Icon, title, color = 'var(--accent-primary)' }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4" style={{ color }} />
      <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
    </div>
  );
}

// ─── Pending request row ──────────────────────────────────────────────────────
function PendingRow({ r, t }) {
  const isDeposit = r.request_type === 'deposit';
  const urgent = r.hours_pending >= 2;
  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-0"
      style={{ borderColor: 'var(--border-color)' }}>
      {/* Type icon */}
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: isDeposit
            ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)'
            : 'color-mix(in srgb, var(--accent-warning) 15%, transparent)',
        }}>
        {isDeposit
          ? <TrendingUp className="w-3.5 h-3.5" style={{ color: 'var(--accent-success)' }} />
          : <TrendingDown className="w-3.5 h-3.5" style={{ color: 'var(--accent-warning)' }} />}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
            {r.user_name || r.email}
          </p>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded capitalize"
            style={{
              backgroundColor: isDeposit
                ? 'color-mix(in srgb, var(--accent-success) 12%, transparent)'
                : 'color-mix(in srgb, var(--accent-warning) 12%, transparent)',
              color: isDeposit ? 'var(--accent-success)' : 'var(--accent-warning)',
            }}>
            {r.request_type}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{methodLabel(r.method)}</p>
          <span style={{ color: 'var(--text-tertiary)' }}>·</span>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>#{r.id}</p>
          {r.has_proof === 1 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-info) 12%, transparent)', color: 'var(--accent-info)' }}>
              {t('common.completed')}
            </span>
          )}
        </div>
      </div>

      {/* Amount + time */}
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
          {fmtUSD(r.amount)}
        </p>
        <p className="text-[10px] flex items-center gap-1 justify-end mt-0.5"
          style={{ color: urgent ? 'var(--accent-danger)' : 'var(--text-tertiary)' }}>
          <Clock className="w-2.5 h-2.5" />
          {hourLabel(r.hours_pending, t)}
        </p>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Reports() {
  const { t } = useLocale();
  const [loading,          setLoading]          = useState(true);
  const [dateRange,        setDateRange]        = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date:   new Date().toISOString().split('T')[0],
  });
  const [reportData,       setReportData]       = useState(null);
  const [riskyAccounts,    setRiskyAccounts]    = useState([]);
  const [pendingRequests,  setPendingRequests]  = useState([]);

  useEffect(() => { loadReports(); }, [dateRange]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [dashboard, risky, pending] = await Promise.all([
        reportsAPI.getDashboard(dateRange),
        reportsAPI.getRiskyAccounts(10),
        reportsAPI.getPendingRequests(),
      ]);
      setReportData(dashboard.data);
      setRiskyAccounts(risky.data || []);
      setPendingRequests(pending.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error(t('errors.general'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  const { financial, trading, users, top_symbols } = reportData || {};
  const deposits    = financial?.deposits    || {};
  const withdrawals = financial?.withdrawals || {};

  // Pending split
  const pendingDeposits    = pendingRequests.filter(r => r.request_type === 'deposit');
  const pendingWithdrawals = pendingRequests.filter(r => r.request_type === 'withdrawal');
  const urgentCount        = pendingRequests.filter(r => r.hours_pending >= 2).length;

  return (
    <div className="space-y-8 p-6 max-w-6xl mx-auto">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('reports.title')}</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{t('common.details')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={dateRange.start_date}
            onChange={e => setDateRange(p => ({ ...p, start_date: e.target.value }))}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{t('common.to')}</span>
          <input type="date" value={dateRange.end_date}
            onChange={e => setDateRange(p => ({ ...p, end_date: e.target.value }))}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <button onClick={loadReports}
            className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium"
            style={{ backgroundColor: 'var(--accent-primary)', color: 'white' }}>
            <RefreshCw className="w-3.5 h-3.5" /> {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* ── Financial Overview ── */}
      <div>
        <SectionHeading icon={DollarSign} title={t('dashboard.financialOverview')} color="var(--accent-success)" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={TrendingUp}   label={t('dashboard.totalDeposits')}    color="var(--accent-success)"
            value={fmtUSD(deposits.total)}
            subtext={`${deposits.count || 0} ${t('dashboard.totalTransactions')} · ${deposits.unique_users || 0} ${t('common.users')}`} />
          <StatCard icon={TrendingDown} label={t('dashboard.totalWithdrawals')} color="var(--accent-danger)"
            value={fmtUSD(withdrawals.total)}
            subtext={`${withdrawals.count || 0} ${t('dashboard.totalTransactions')} · ${withdrawals.approval_rate || 0}% ${t('common.approved')}`} />
          <StatCard icon={DollarSign}   label={t('reports.profitability')}
            color={financial?.net_cash_flow >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)'}
            value={fmtUSD(financial?.net_cash_flow)}
            subtext={`${financial?.deposit_withdrawal_ratio || '0.00'}:1`} />
          <StatCard icon={Users}        label={t('dashboard.totalClients')}       color="var(--accent-info)"
            value={users?.total_users || 0}
            subtext={`${users?.active_users || 0} ${t('common.active')} · ${users?.kyc_approved || 0} ${t('common.approved')}`} />
        </div>
      </div>

      {/* ── Pending Requests ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: 'var(--accent-warning)' }} />
            <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('common.pending')}</h2>
            {pendingRequests.length > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)', color: 'var(--accent-warning)' }}>
                {pendingRequests.length} {t('common.pending')}
              </span>
            )}
            {urgentCount > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)', color: 'var(--accent-danger)' }}>
                {urgentCount} {t('common.warning')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
            <span>{pendingDeposits.length} {t('deposits.title')}</span>
            <span>·</span>
            <span>{pendingWithdrawals.length} {t('withdrawals.title')}</span>
          </div>
        </div>

        <div className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          {pendingRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <CheckCircle className="w-8 h-8" style={{ color: 'var(--accent-success)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>{t('kyc.allCaughtUp')}</p>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{t('kyc.noApplications')}</p>
            </div>
          ) : (
            <div className="px-4">
              {pendingRequests.map((r, i) => <PendingRow key={`${r.request_type}-${r.id}-${i}`} r={r} t={t} />)}
            </div>
          )}
        </div>
      </div>

      {/* ── Account Overview ── */}
      <div>
        <SectionHeading icon={BarChart2} title={t('dashboard.accountOverview')} color="var(--accent-primary)" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <MetricCard label={t('dashboard.totalAccounts')}    icon={Users}      value={trading?.total_accounts || 0} />
          <MetricCard label={t('accounts.balance')}     icon={DollarSign} value={fmtUSD(trading?.total_balance)}    color="var(--accent-success)" />
          <MetricCard label={t('accounts.equity')}      icon={TrendingUp} value={fmtUSD(trading?.total_equity)}     color="var(--accent-success)" />
          <MetricCard label={t('accounts.credit')}      icon={CreditCard} value={fmtUSD(trading?.total_credit_given)} />
          <MetricCard label={t('accounts.margin')}       icon={Activity}   value={fmtUSD(trading?.total_margin_used)} />
          <MetricCard label={t('accounts.freeMargin')}       icon={Wallet}     value={fmtUSD(trading?.total_free_margin)} color="var(--accent-info)" />
          <MetricCard label={t('dashboard.openPositions')}    icon={TrendingUp} value={trading?.total_open_positions || 0} />
          <MetricCard label={t('trading.pnl')}    icon={TrendingUp}
            value={fmtUSD(trading?.total_unrealized_pnl)}
            color={trading?.total_unrealized_pnl >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)'} />
        </div>
      </div>

      {/* ── Risk Summary ── */}
      {(trading?.accounts_at_stopout > 0 || trading?.accounts_at_margin_call > 0 || trading?.accounts_high_risk > 0) && (
        <div>
          <SectionHeading icon={AlertTriangle} title={t('dashboard.riskAlerts')} color="var(--accent-danger)" />
          <div className="grid grid-cols-3 gap-3">
            <div className="p-4 rounded-xl border"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-danger)' }}>{t('settings.stopOut')}</p>
              <p className="text-2xl font-bold font-mono mt-1" style={{ color: 'var(--accent-danger)' }}>{trading?.accounts_at_stopout || 0}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{t('accounts.accountsList')}</p>
            </div>
            <div className="p-4 rounded-xl border"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-warning) 25%, transparent)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>{t('settings.marginCall')}</p>
              <p className="text-2xl font-bold font-mono mt-1" style={{ color: 'var(--accent-warning)' }}>{trading?.accounts_at_margin_call || 0}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{t('accounts.accountsList')}</p>
            </div>
            <div className="p-4 rounded-xl border"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-warning) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-warning) 25%, transparent)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>{t('common.warning')}</p>
              <p className="text-2xl font-bold font-mono mt-1" style={{ color: 'var(--accent-warning)' }}>{trading?.accounts_high_risk || 0}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{t('accounts.accountsList')}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── User Stats ── */}
      <div>
        <SectionHeading icon={Users} title={t('common.status')} color="var(--accent-info)" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label={t('dashboard.totalClients')}        icon={Users}      value={users?.total_users || 0} />
          <MetricCard label={t('dashboard.activeUsers')}       icon={UserCheck}  value={users?.active_users || 0}    color="var(--accent-success)" />
          <MetricCard label={t('common.email')}     icon={CheckCircle} value={users?.email_verified_users || 0} color="var(--accent-success)" />
          <MetricCard label={t('clients.kycStatus')}       icon={Shield}     value={users?.kyc_approved || 0}    color="var(--accent-success)" />
          <MetricCard label={t('common.pending')}        icon={Clock}      value={users?.kyc_pending || 0}     color="var(--accent-warning)" />
          <MetricCard label={t('common.rejected')}       icon={XCircle}    value={users?.kyc_rejected || 0}    color="var(--accent-danger)" />
          <MetricCard label={t('common.status')}      icon={UserX}      value={users?.kyc_not_submitted || 0} />
          <MetricCard label={t('dashboard.activeThisMonth')}  icon={Activity}   value={users?.logged_in_this_month || 0} color="var(--accent-info)" />
        </div>
      </div>

      {/* ── Deposit Stats Detail ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={TrendingUp} title={t('deposits.title')} color="var(--accent-success)" />
          <div className="space-y-2">
            {[
              { label: t('common.total'),   value: fmtUSD(deposits.total) },
              { label: t('common.total'),       value: deposits.count || 0 },
              { label: t('deposits.averageDeposit'),   value: fmtUSD(deposits.average) },
              { label: t('common.amount'),   value: fmtUSD(deposits.max) },
              { label: t('common.amount'),  value: fmtUSD(deposits.min) },
              { label: t('common.users'), value: deposits.unique_users || 0 },
              { label: t('common.pending'),    value: fmtUSD(deposits.pending) },
              { label: t('common.pending'),     value: deposits.pending_count || 0 },
              { label: t('common.approved'),     value: `${deposits.approval_rate || 0}%` },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-1.5 border-b last:border-0"
                style={{ borderColor: 'var(--border-color)' }}>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <span className="text-sm font-semibold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={TrendingDown} title={t('withdrawals.title')} color="var(--accent-danger)" />
          <div className="space-y-2">
            {[
              { label: t('common.total'),      value: fmtUSD(withdrawals.total) },
              { label: t('common.total'),           value: withdrawals.count || 0 },
              { label: t('withdrawals.averageWithdrawal'),    value: fmtUSD(withdrawals.average) },
              { label: t('common.amount'),    value: fmtUSD(withdrawals.max) },
              { label: t('common.amount'),   value: fmtUSD(withdrawals.min) },
              { label: t('common.users'),    value: withdrawals.unique_users || 0 },
              { label: t('common.pending'),        value: fmtUSD(withdrawals.pending) },
              { label: t('common.pending'),         value: withdrawals.pending_count || 0 },
              { label: t('common.approved'),         value: `${withdrawals.approval_rate || 0}%` },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-1.5 border-b last:border-0"
                style={{ borderColor: 'var(--border-color)' }}>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <span className="text-sm font-semibold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Top Symbols ── */}
      {top_symbols && top_symbols.length > 0 && (
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={BarChart2} title={t('reports.exposure')} color="var(--accent-primary)" />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                  {[t('instruments.symbol'), t('trading.positions'), t('reports.netVolume'), t('reports.totalExposure'), t('trading.pnl')].map(h => (
                    <th key={h} className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wide"
                      style={{ color: 'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top_symbols.map((s, i) => (
                  <tr key={i} className="border-b last:border-0" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="py-3 px-3 font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{s.symbol}</td>
                    <td className="py-3 px-3 font-mono text-sm" style={{ color: 'var(--text-primary)' }}>{s.position_count}</td>
                    <td className="py-3 px-3 font-mono text-sm"
                      style={{ color: parseFloat(s.net_volume) >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                      {parseFloat(s.net_volume).toFixed(2)}
                    </td>
                    <td className="py-3 px-3 font-mono text-sm" style={{ color: 'var(--text-primary)' }}>
                      {fmtUSD(s.net_exposure)}
                    </td>
                    <td className="py-3 px-3 font-mono text-sm"
                      style={{ color: parseFloat(s.total_pnl) >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                      {fmtUSD(s.total_pnl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Risky Accounts ── */}
      {riskyAccounts.length > 0 && (
        <div className="p-5 rounded-xl border"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <SectionHeading icon={AlertTriangle} title={t('dashboard.riskAlerts')} color="var(--accent-warning)" />
          <div className="space-y-2">
            {riskyAccounts.map((a) => (
              <div key={a.user_id} className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{a.name || a.email}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{a.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold"
                    style={{ color: ['STOP_OUT', 'MARGIN_CALL'].includes(a.risk_level) ? 'var(--accent-danger)' : 'var(--accent-warning)' }}>
                    {a.risk_level}
                  </p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                    {t('accounts.margin')}: {parseFloat(a.margin_level).toFixed(0)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}